import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, XCircle, Minus, ArrowLeft, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const COMPARISONS = {
  "smallseotools": {
    name: "SmallSEOTools",
    tagline: "MarkeeMix vs SmallSEOTools — Which Free SEO Platform Is Better in 2026?",
    intro: "SmallSEOTools has been a go-to free SEO toolkit for years, offering dozens of simple tools for keyword checking, plagiarism detection, and backlink analysis. MarkeeMix takes a different approach — combining 75+ tools across SEO, content, social media, YouTube, PDF, and design in one modern platform with AI-powered features. Here is a detailed feature-by-feature comparison to help you choose.",
    features: [
      { name: "SEO Tools", mk: true, comp: true, note: "Both offer keyword research, backlink checker, site audit. MarkeeMix adds AI-powered suggestions." },
      { name: "AI Content Writer", mk: true, comp: false, note: "MarkeeMix includes AI blog generator and article rewriter." },
      { name: "YouTube SEO Tools", mk: true, comp: false, note: "Tag generator, title optimizer, thumbnail analyzer — exclusive to MarkeeMix." },
      { name: "Social Media Tools", mk: true, comp: false, note: "Hashtag generator, post scheduler, Instagram analytics." },
      { name: "PDF Tools (40+)", mk: true, comp: false, note: "Merge, split, convert, compress — all in-browser." },
      { name: "Design Tools", mk: true, comp: false, note: "Image editor, color palette, brand kit generator." },
      { name: "Plagiarism Checker", mk: true, comp: true, note: "Both offer plagiarism detection." },
      { name: "Modern UI/UX", mk: true, comp: false, note: "MarkeeMix has a clean, modern interface vs dated design." },
      { name: "No Ads on Free Tools", mk: true, comp: false, note: "SmallSEOTools shows heavy advertising on free pages." },
      { name: "Dashboard & Analytics", mk: true, comp: false, note: "Unified dashboard to track all your marketing metrics." },
      { name: "Free Without Signup", mk: true, comp: true, note: "Both offer free tools without requiring an account." },
      { name: "Google Ads Tools", mk: true, comp: false, note: "Campaign planner and ad optimizer." },
    ],
    verdict: "SmallSEOTools is a solid choice if you only need basic SEO checks. MarkeeMix is the better platform if you want a comprehensive marketing suite — SEO, content, social media, YouTube, PDF, and design tools in one place, without ads and with a modern experience."
  },
  "prepostseo": {
    name: "Prepostseo",
    tagline: "MarkeeMix vs Prepostseo — Complete Feature Comparison 2026",
    intro: "Prepostseo offers a collection of free online tools primarily focused on SEO and content checking. MarkeeMix expands on this concept by offering 75+ tools across multiple marketing categories with AI integration. Here is how they compare across key features.",
    features: [
      { name: "SEO Analysis Tools", mk: true, comp: true, note: "Both offer DA checker, backlink analysis, keyword tools." },
      { name: "AI Blog Generator", mk: true, comp: false, note: "Generate complete SEO articles with AI." },
      { name: "YouTube Optimization", mk: true, comp: false, note: "Video title, tag, and thumbnail optimization." },
      { name: "Social Media Suite", mk: true, comp: true, note: "MarkeeMix offers deeper analytics and scheduling." },
      { name: "PDF Tools", mk: true, comp: true, note: "Both offer PDF operations. MarkeeMix has 40+ tools." },
      { name: "Plagiarism Checker", mk: true, comp: true, note: "Both offer content uniqueness checking." },
      { name: "Content Rewriter", mk: true, comp: true, note: "MarkeeMix uses AI for smarter rewriting." },
      { name: "Brand Kit Generator", mk: true, comp: false, note: "Create complete brand identity in minutes." },
      { name: "Site Audit (100+ checks)", mk: true, comp: false, note: "Comprehensive multi-page audit with Core Web Vitals." },
      { name: "Ad-Free Experience", mk: true, comp: false, note: "Clean interface without intrusive advertising." },
      { name: "Team Collaboration", mk: true, comp: false, note: "Pro plan includes team features." },
    ],
    verdict: "Prepostseo covers basic SEO and content needs. MarkeeMix offers significantly more depth with AI-powered tools, YouTube SEO, design capabilities, and a professional ad-free experience. For serious marketers, MarkeeMix is the clear upgrade."
  },
  "ubersuggest": {
    name: "Ubersuggest",
    tagline: "MarkeeMix vs Ubersuggest — Which SEO Tool Gives More Value in 2026?",
    intro: "Ubersuggest, created by Neil Patel, is a popular SEO tool known for keyword research and site auditing. While Ubersuggest focuses primarily on SEO, MarkeeMix offers a broader toolkit spanning SEO, content, social media, YouTube, PDF, and design. The pricing models also differ significantly.",
    features: [
      { name: "Keyword Research", mk: true, comp: true, note: "Both offer keyword data. Ubersuggest has deeper historical data." },
      { name: "Site Audit", mk: true, comp: true, note: "Both provide comprehensive audits. MarkeeMix includes security checks." },
      { name: "Backlink Analysis", mk: true, comp: true, note: "Both analyze backlink profiles." },
      { name: "Content Marketing Tools", mk: true, comp: false, note: "AI writer, rewriter, readability checker — MarkeeMix exclusive." },
      { name: "YouTube SEO", mk: true, comp: false, note: "Complete YouTube optimization toolkit." },
      { name: "Social Media Tools", mk: true, comp: false, note: "Hashtag generator, scheduler, Instagram analytics." },
      { name: "PDF Tools", mk: true, comp: false, note: "40+ PDF operations not available in Ubersuggest." },
      { name: "Design Tools", mk: true, comp: false, note: "Image editor, brand kit — not in Ubersuggest." },
      { name: "Free Tier Generosity", mk: true, comp: false, note: "MarkeeMix free tier is more generous with daily limits." },
      { name: "Pricing", mk: true, comp: true, note: "Ubersuggest: $29-99/mo. MarkeeMix Pro is more affordable." },
      { name: "Chrome Extension", mk: false, comp: true, note: "Ubersuggest has a Chrome extension for SERP data." },
    ],
    verdict: "Ubersuggest is excellent for dedicated SEO research with deep keyword data. MarkeeMix wins on breadth — if you need SEO plus content, social, YouTube, PDF, and design tools in one subscription, MarkeeMix offers far more value per dollar."
  }
};

export default function ComparisonPage() {
  const { competitor } = useParams();
  const data = COMPARISONS[competitor];

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold mb-6">MarkeeMix Comparisons</h1>
          <p className="text-gray-600 mb-8">See how MarkeeMix compares to other marketing tools:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(COMPARISONS).map(([slug, c]) => (
              <Link key={slug} to={`/compare/${slug}`}>
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
                  <h2 className="text-lg font-bold mb-2">MarkeeMix vs {c.name}</h2>
                  <p className="text-sm text-gray-500">{c.intro.substring(0, 120)}...</p>
                  <span className="text-violet-600 text-sm mt-3 inline-block">Read comparison →</span>
                </Card>
              </Link>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/compare" className="text-violet-600 text-sm flex items-center gap-1 mb-6 hover:underline">
          <ArrowLeft className="w-4 h-4" /> All Comparisons
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{data.tagline}</h1>
        <p className="text-gray-600 leading-relaxed mb-8">{data.intro}</p>

        {/* Comparison Table */}
        <Card className="overflow-hidden mb-8">
          <div className="grid grid-cols-3 bg-gray-100 p-4 font-semibold text-sm">
            <span>Feature</span>
            <span className="text-center text-violet-700">MarkeeMix</span>
            <span className="text-center">{data.name}</span>
          </div>
          {data.features.map((f, i) => (
            <div key={i} className={`grid grid-cols-3 p-4 items-center text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-t`}>
              <div>
                <span className="font-medium">{f.name}</span>
                {f.note && <p className="text-xs text-gray-400 mt-1">{f.note}</p>}
              </div>
              <div className="text-center">
                {f.mk ? <CheckCircle className="w-5 h-5 text-green-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />}
              </div>
              <div className="text-center">
                {f.comp ? <CheckCircle className="w-5 h-5 text-green-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />}
              </div>
            </div>
          ))}
        </Card>

        {/* Verdict */}
        <Card className="p-6 bg-violet-50 border-violet-200 mb-8">
          <h2 className="text-lg font-bold text-violet-800 mb-2">Our Verdict</h2>
          <p className="text-gray-700">{data.verdict}</p>
        </Card>

        {/* CTA */}
        <div className="text-center py-8">
          <h2 className="text-xl font-bold mb-3">Ready to try MarkeeMix?</h2>
          <p className="text-gray-500 mb-6">75+ tools, most free, no signup required</p>
          <div className="flex gap-4 justify-center">
            <Link to="/tools"><Button className="bg-violet-600 hover:bg-violet-700">Explore All Tools</Button></Link>
            <Link to="/pricing"><Button variant="outline">View Pricing</Button></Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
